import dayjs from 'dayjs';
import React, { forwardRef } from 'react';

import { EMPTY_FUNCTION } from '@travel/traveler-core/constants';
import { cx } from '@travel/utils';

import CalendarBody from '../CalendarBody';
import CalendarLabel from '../CalendarLabel';
import CalendarWeekHeader from '../CalendarWeekHeader';

import styles from './datePicker.module.scss';

import type { ConfigType as DateType, Dayjs } from 'dayjs';
import type {
  Ref,
  CSSProperties,
  MouseEvent,
  KeyboardEvent,
  FocusEvent,
  ComponentType,
} from 'react';
import type { CalendarBodyHandle } from '../CalendarBody';
import type { PerDayComponentProps } from './';
type Props = {
  /** Custom style for wrapper */
  className?: string;
  style?: CSSProperties;
  /** Date to define the calendar month and year
   * (eg. 2019-10-29, the calendar will be rendered as October, 2019 )
   */
  calendarDate: DateType;
  /** Custom style for calendar day */
  dayItemClassName?: string;
  /** End date of selected range */
  endDate?: string;
  /** Date which is being hovered */
  hoveredDate?: string;
  /** Date to reference (usually, be used as current date) */
  referenceDate?: string;
  /** Start date of selected range */
  startDate?: string;
  /** callback function when calendar day is clicked */
  onClickDayItem?: (event: MouseEvent | KeyboardEvent, day: Dayjs) => void;
  /** callback function when calendar day is being hover */
  onHoverDayItem?: (event: MouseEvent | FocusEvent, day?: Dayjs) => void;
  /** Flag to define whether the CalendarWeekHeader will be shown on calendars or not */
  isShowWeekHeader?: boolean;
  /** week type:
   * 'week': start from Sunday
   * 'isoWeek': start from Monday
   */
  weekType?: 'week' | 'isoWeek';
  /** Array of string to define title of day (normal-ordering) */
  weekdayLabels?: string[];
  /** Array of string to define title of month */
  monthLabels?: string[];
  /** Date to end of reference */
  referenceEndDate?: DateType;
  /** Custom style for wrapper of calendar day */
  dayWrapperClassName?: string;
  /** Extra component to display on calendar per day */
  PerDayComponent?: ComponentType<PerDayComponentProps>;
  /** Custom format for display */
  labelDateFormat?: string;
  /** Custom style for wrapper of calendar month labels */
  calendarLabelClassName?: string;
  /** Custom style for wrapper of calendar week headers */
  calendarWeekHeaderClassName?: string;
  /** Custom style for calendar body wrapper (table) */
  calendarBodyClass?: string;
  /** Date Selection Highlight type (default circle) */
  highlightType?: 'circle' | 'full';
  /** Flag to highlight the current date */
  shouldShowCurrentDate?: boolean;
  /** Allows selection of days outside of current month. Outdated days still cannot be selected */
  shouldAllowInvalidMonthDays?: boolean;
  /** List of holidays */
  holidays?: Dayjs[] | string[];
  /** Flag to get all day states. A day could have multiple states, e.g. invalidMonth, weekend, holiday.
   * Defaults to false in which case certain states take priority. */
  shouldGetAllDayStates?: boolean;
  /** enable the selected range which after end date or before start date */
  shouldEnableInvalidRange?: boolean;
};

const Calendar = forwardRef<CalendarBodyHandle, Props>(function Calendar(
  props: Props,
  ref: Ref<CalendarBodyHandle>,
) {
  const {
    className = '',
    dayItemClassName = '',
    onClickDayItem = EMPTY_FUNCTION,
    style,
    calendarDate,
    isShowWeekHeader = true,
    weekType,
    weekdayLabels,
    monthLabels,
    labelDateFormat,
    calendarWeekHeaderClassName,
    calendarBodyClass,
    calendarLabelClassName,
    ...rest
  } = props;

  const day = dayjs(calendarDate);
  const accessibilityId = '' + monthLabels?.[parseInt(day.format('M')) - 1];

  return (
    <div className={cx(className, styles.calendar)} style={style}>
      <CalendarLabel
        className={cx(styles.calendarLabel, calendarLabelClassName)}
        calendarDate={calendarDate}
        monthLabels={monthLabels}
        labelDateFormat={labelDateFormat}
        id={accessibilityId}
      />
      {isShowWeekHeader && (
        <CalendarWeekHeader
          className={cx(styles.calendarWeekHeader, calendarWeekHeaderClassName)}
          itemClassName={styles.calendarWeekHeaderItem}
          weekType={weekType}
          weekdayLabels={weekdayLabels}
        />
      )}
      <CalendarBody
        className={cx(styles.calendarBody, calendarBodyClass)}
        calendarDate={calendarDate}
        weekType={weekType}
        ariaDescribedBy={accessibilityId}
        ref={ref}
        onClickDayItem={onClickDayItem}
        {...rest}
      />
    </div>
  );
});

export default React.memo(Calendar);
